import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { industriesData } from "@/mocks/industriesData";
import { useSEO } from "@/hooks/useSEO";

export default function Industries() {
  useSEO({
    title: "Industries We Serve Malaysia | Law Enforcement, Government, Healthcare, Logistics | SE Distribution",
    description: "SE Distribution serves law enforcement, government, healthcare, logistics, defence & enterprise IT in Malaysia with body cameras, rugged devices, HCI infrastructure & backup solutions.",
    canonical: "https://www.sedistribution.com.my/industries",
    keywords: "law enforcement technology Malaysia, government IT Malaysia, healthcare technology Malaysia, logistics technology Malaysia, defence technology Malaysia, enterprise IT Malaysia",
  });
  const [activeId, setActiveId] = useState<string | null>(null);

  const active = industriesData.find((i) => i.id === activeId) ?? industriesData[0];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Hero */}
        <div className="relative bg-[#1a1a1a] py-20 overflow-hidden">
          <img
            src="https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/032e2ad3bfce6ee12450075c1af2aaa4.jpeg"
            alt="Industries"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/60 via-[#1a1a1a]/40 to-[#1a1a1a]/80"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <span className="text-xs font-semibold tracking-widest uppercase text-white/50 mb-3 block">
              Industries We Serve
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
              Technology Solutions for <br className="hidden md:block" />
              <strong>Every Industry</strong>
            </h1>
            <p className="text-white/70 text-sm max-w-xl mx-auto leading-relaxed">
              SE Distribution delivers purpose-built technology solutions across six key industries in Malaysia — from law enforcement to enterprise IT. Explore how we solve real challenges for real organizations.
            </p>
          </div>
        </div>

        {/* Industry Tabs */}
        <div className="sticky top-16 lg:top-20 z-40 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {industriesData.map((ind) => (
                <button
                  key={ind.id}
                  onClick={() => setActiveId(ind.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                    (activeId ?? industriesData[0].id) === ind.id
                      ? "bg-[#1a1a1a] text-white"
                      : "text-gray-500 hover:text-[#1a1a1a] hover:bg-gray-50"
                  }`}
                >
                  <i className={`${ind.icon} text-sm`}></i>
                  {ind.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active Industry Detail */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          {/* Industry Hero Image */}
          <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-12">
            <img
              src={active.heroImage}
              alt={active.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg">
                  <i className={`${active.icon} text-[#1a1a1a] text-xl`}></i>
                </div>
                <span className="text-xs font-semibold tracking-widest uppercase text-gray-400">{active.tagline}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">{active.title}</h2>
              <p className="text-gray-500 text-sm leading-relaxed">{active.description}</p>
            </div>

            {/* Outcomes */}
            <div className="flex flex-row lg:flex-col gap-4 flex-shrink-0">
              {active.outcomes.map((o) => (
                <div key={o.label} className="bg-[#f9f7f4] rounded-lg p-4 text-center min-w-[120px]">
                  <p className="text-2xl font-bold text-[#1a1a1a]">{o.metric}</p>
                  <p className="text-xs text-gray-500 mt-1 leading-tight">{o.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Challenges */}
          <div className="mb-14">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-5">Key Challenges We Solve</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {active.challenges.map((c, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#fafafa] border border-gray-100 rounded-lg p-4">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{c}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Solutions */}
          <div className="mb-14">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-5">Our Solutions for {active.title}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {active.solutions.map((sol) => (
                <div key={sol.title} className="border border-gray-100 rounded-lg p-6 hover:border-[#1a1a1a]/20 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
                      <i className={`${sol.icon} text-[#1a1a1a] text-lg`}></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-[#1a1a1a] text-sm">{sol.title}</h4>
                        <span className="text-xs bg-[#1a1a1a]/5 text-[#1a1a1a] px-2 py-0.5 rounded-full font-medium">{sol.brand}</span>
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">{sol.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Featured Brands */}
          <div className="mb-14">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-5">Featured Brand Partners</h3>
            <div className="flex flex-wrap gap-3">
              {active.featuredBrands.map((b) => (
                <Link
                  key={b}
                  to="/brands"
                  className="flex items-center gap-2 border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-medium text-[#1a1a1a] hover:border-[#1a1a1a] hover:bg-[#fafafa] transition-all cursor-pointer"
                >
                  <i className="ri-award-line text-gray-400 text-sm"></i>
                  {b}
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#1a1a1a] rounded-xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Ready to solve your {active.title} challenges?</h3>
              <p className="text-white/60 text-sm">Talk to our solutions team and get a tailored recommendation for your organization.</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link
                to="/contact"
                className="bg-white text-[#1a1a1a] text-sm font-semibold px-6 py-3 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Send an Enquiry
              </Link>
              <a
                href="tel:+60364127917"
                className="border border-white/30 text-white text-sm font-medium px-6 py-3 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                +603 6412 7917
              </a>
            </div>
          </div>
        </div>

        {/* All Industries Grid */}
        <div className="bg-[#f9f7f4] py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h3 className="text-xl font-bold text-[#1a1a1a] mb-8 text-center">All Industries</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {industriesData.map((ind) => (
                <button
                  key={ind.id}
                  onClick={() => { setActiveId(ind.id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all cursor-pointer text-center ${
                    (activeId ?? industriesData[0].id) === ind.id
                      ? "bg-[#1a1a1a] border-[#1a1a1a] text-white"
                      : "bg-white border-gray-100 text-[#1a1a1a] hover:border-[#1a1a1a]/20"
                  }`}
                >
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className={`${ind.icon} text-2xl`}></i>
                  </div>
                  <span className="text-xs font-medium leading-tight">{ind.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
